import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  getSelfServiceChatApiUrl,
  getSelfServiceGeneratedDashboardSaveApiUrl,
  unwrapObject,
} from './self-service-api.utils';

export interface SelfServiceChatFieldOption {
  label: string;
  value: string;
}

export interface SelfServiceChatMissingField {
  key: string;
  label: string;
  options: SelfServiceChatFieldOption[];
  source: 'response' | 'columns';
}

export interface SelfServiceChatAnalysisResult {
  message: string;
  missingFields: SelfServiceChatMissingField[];
  submitPayloadTemplate: Record<string, unknown> | null;
  dashboardName: string;
  dashboardSlug: string;
  raw: unknown;
}

export interface SelfServiceChatSaveResult {
  message: string;
  dashboardSlug: string;
  raw: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class SelfServiceChatService {
  constructor(private readonly http: HttpClient) {}

  analyzeFiles(
    files: File[],
    payload: {
      dashboardName?: string;
      dashboardSlug?: string;
      availableColumns?: string[];
    } = {},
  ): Observable<SelfServiceChatAnalysisResult> {
    const formData = new FormData();
    const fileDashboardName = this.getDashboardNameFromFiles(files);
    const dashboardName = fileDashboardName || (payload.dashboardName || '').trim() || 'Final';
    const dashboardSlug = this.getDashboardSlugFromName(fileDashboardName || dashboardName)
      || (payload.dashboardSlug || '').trim()
      || 'final';

    files.forEach((file) => {
      formData.append('ppt', file, file.name);
    });
    formData.append('dashboardName', dashboardName);
    formData.append('dashboardSlug', dashboardSlug);

    return this.http.post<unknown>(getSelfServiceChatApiUrl(), formData).pipe(
      map((response) => this.normalizeAnalysisResponse(
        response,
        payload.availableColumns || [],
        dashboardName,
        dashboardSlug,
      )),
      catchError((error) => {
        const message =
          this.extractMessage(error?.error)
          || (typeof error?.message === 'string' ? error.message : '')
          || 'Unable to analyze the uploaded file.';
        return throwError(() => new Error(message));
      }),
    );
  }

  private normalizeAnalysisResponse(
    response: unknown,
    availableColumns: string[],
    dashboardName: string,
    dashboardSlug: string,
  ): SelfServiceChatAnalysisResult {
    const nestedResponse =
      unwrapObject<Record<string, unknown>>(response, ['data', 'result', 'analysis', 'payload'])
      || null;
    const candidates = [response, nestedResponse].filter(
      (candidate): candidate is Record<string, unknown> =>
        !!candidate && typeof candidate === 'object' && !Array.isArray(candidate),
    );

    const message =
      candidates
        .map((candidate) => this.extractMessage(candidate))
        .find((candidate) => !!candidate)
      || (this.extractSubmitPayloadTemplate(candidates)
        ? 'Template generated successfully. Review and edit the DVM JSON below.'
        : '')
      || (availableColumns.length
        ? 'File analyzed. Map the missing fields below.'
        : 'File analyzed successfully.');

    const missingFields = this.normalizeMissingFields(candidates, availableColumns);
    const submitPayloadTemplate = this.extractSubmitPayloadTemplate(candidates);

    return {
      message,
      missingFields,
      submitPayloadTemplate,
      dashboardName,
      dashboardSlug,
      raw: response,
    };
  }

  saveGeneratedDashboard(
    dashboardSlug: string,
    template: Record<string, unknown>,
    workflow = 'fulfillment',
  ): Observable<SelfServiceChatSaveResult> {
    return this.http.post<unknown>(
      getSelfServiceGeneratedDashboardSaveApiUrl(dashboardSlug, workflow),
      template,
    ).pipe(
      map((response) => {
        const nestedResponse =
          unwrapObject<Record<string, unknown>>(response, ['data', 'result', 'payload'])
          || null;
        const candidates = [response, nestedResponse].filter(
          (candidate): candidate is Record<string, unknown> =>
            !!candidate && typeof candidate === 'object' && !Array.isArray(candidate),
        );
        const message =
          candidates
            .map((candidate) => this.extractMessage(candidate))
            .find((candidate) => !!candidate)
          || 'Generated dashboard JSON saved successfully.';

        return {
          message,
          dashboardSlug,
          raw: response,
        };
      }),
      catchError((error) => {
        const message =
          this.extractMessage(error?.error)
          || (typeof error?.message === 'string' ? error.message : '')
          || 'Unable to save the edited dashboard JSON.';
        return throwError(() => new Error(message));
      }),
    );
  }

  private extractSubmitPayloadTemplate(
    candidates: Record<string, unknown>[],
  ): Record<string, unknown> | null {
    for (const candidate of candidates) {
      const rawTemplate =
        candidate['submitPayloadTemplate']
        ?? candidate['submit_payload_template']
        ?? candidate['payloadTemplate']
        ?? candidate['template']
        ?? candidate['response']
        ?? candidate['config']
        ?? candidate['dashboardJson']
        ?? candidate['dashboardConfig'];
      const parsedTemplate = this.parseTemplateObject(rawTemplate);
      if (parsedTemplate) {
        return parsedTemplate;
      }

      if (this.isTemplateLike(candidate)) {
        return candidate;
      }

      const nestedTemplate = this.findNestedTemplate(candidate);
      if (nestedTemplate) {
        return nestedTemplate;
      }
    }

    return null;
  }

  private normalizeMissingFields(
    candidates: Record<string, unknown>[],
    availableColumns: string[],
  ): SelfServiceChatMissingField[] {
    for (const candidate of candidates) {
      const rawMissingFields =
        candidate['missingFields']
        ?? candidate['missing_fields']
        ?? candidate['missingFieldList']
        ?? candidate['fields']
        ?? candidate['fieldMappings'];

      const normalized = this.parseMissingFields(rawMissingFields, availableColumns);
      if (normalized.length) {
        return normalized;
      }
    }

    return [];
  }

  private parseMissingFields(
    rawMissingFields: unknown,
    availableColumns: string[],
  ): SelfServiceChatMissingField[] {
    const fallbackOptions = this.normalizeOptions(availableColumns);

    if (Array.isArray(rawMissingFields)) {
      return rawMissingFields
        .map((entry) => this.parseMissingFieldEntry(entry, fallbackOptions))
        .filter((entry): entry is SelfServiceChatMissingField => !!entry);
    }

    if (rawMissingFields && typeof rawMissingFields === 'object') {
      return Object.entries(rawMissingFields as Record<string, unknown>)
        .map(([key, value]) => {
          const entry = typeof value === 'object' && value !== null && !Array.isArray(value)
            ? { key, ...(value as Record<string, unknown>) }
            : { key, options: value };
          return this.parseMissingFieldEntry(entry, fallbackOptions);
        })
        .filter((entry): entry is SelfServiceChatMissingField => !!entry);
    }

    return [];
  }

  private parseMissingFieldEntry(
    entry: unknown,
    fallbackOptions: SelfServiceChatFieldOption[],
  ): SelfServiceChatMissingField | null {
    if (typeof entry === 'string') {
      return {
        key: entry,
        label: this.toLabel(entry),
        options: fallbackOptions,
        source: 'columns',
      };
    }

    if (!entry || typeof entry !== 'object') {
      return null;
    }

    const record = entry as Record<string, unknown>;
    const key = String(
      record['key']
      ?? record['field']
      ?? record['name']
      ?? record['attributeName']
      ?? record['columnName']
      ?? record['label']
      ?? '',
    ).trim();

    if (!key) {
      return null;
    }

    const responseOptions = this.normalizeOptions(
      record['options']
      ?? record['values']
      ?? record['dropdownValues']
      ?? record['allowedValues']
      ?? [],
    );
    const options = responseOptions.length ? responseOptions : fallbackOptions;

    return {
      key,
      label: String(record['label'] ?? this.toLabel(key)).trim(),
      options,
      source: responseOptions.length ? 'response' : 'columns',
    };
  }

  private normalizeOptions(values: unknown): SelfServiceChatFieldOption[] {
    if (!Array.isArray(values)) {
      return [];
    }

    const seen = new Set<string>();
    const options: SelfServiceChatFieldOption[] = [];

    values.forEach((value) => {
      const normalized = value && typeof value === 'object'
        ? {
            value: String(
              (value as Record<string, unknown>)['value']
              ?? (value as Record<string, unknown>)['key']
              ?? (value as Record<string, unknown>)['name']
              ?? (value as Record<string, unknown>)['label']
              ?? '',
            ).trim(),
            label: String(
              (value as Record<string, unknown>)['label']
              ?? (value as Record<string, unknown>)['name']
              ?? (value as Record<string, unknown>)['value']
              ?? (value as Record<string, unknown>)['key']
              ?? '',
            ).trim(),
          }
        : {
            value: String(value ?? '').trim(),
            label: String(value ?? '').trim(),
          };

      if (!normalized.value || seen.has(normalized.value)) {
        return;
      }

      seen.add(normalized.value);
      options.push(normalized);
    });

    return options;
  }

  private extractMessage(value: unknown): string {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return '';
    }

    const record = value as Record<string, unknown>;
    const message = record['message']
      ?? record['reply']
      ?? record['summary']
      ?? record['description']
      ?? record['assistantMessage'];
    return typeof message === 'string' ? message.trim() : '';
  }

  private getDashboardNameFromFiles(files: File[]): string {
    const fileName = String(files[0]?.name || '').trim();
    if (!fileName) {
      return '';
    }

    return fileName.replace(/\.[^.]+$/, '').trim();
  }

  private getDashboardSlugFromName(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private toLabel(value: string): string {
    return value
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  private parseTemplateObject(value: unknown): Record<string, unknown> | null {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }

    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? parsed as Record<string, unknown>
          : null;
      } catch {
        return null;
      }
    }

    return null;
  }

  private isTemplateLike(value: unknown): value is Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const record = value as Record<string, unknown>;
    const api = record['api'];
    const integrations = record['integrations'];
    const pageContent = record['pageContent'];

    return (
      (!!api && typeof api === 'object' && !Array.isArray(api) && Array.isArray(integrations))
      || Array.isArray(pageContent)
    );
  }

  private findNestedTemplate(
    value: unknown,
    visited = new Set<unknown>(),
  ): Record<string, unknown> | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    if (visited.has(value)) {
      return null;
    }
    visited.add(value);

    if (this.isTemplateLike(value)) {
      return value;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.findNestedTemplate(item, visited);
        if (found) {
          return found;
        }
      }
      return null;
    }

    const record = value as Record<string, unknown>;
    const templateKeys = [
      'submitPayloadTemplate',
      'submit_payload_template',
      'payloadTemplate',
      'template',
      'dashboardJson',
      'dashboardConfig',
    ];

    for (const key of templateKeys) {
      const parsed = this.parseTemplateObject(record[key]);
      if (parsed) {
        return parsed;
      }
    }

    for (const nested of Object.values(record)) {
      const found = this.findNestedTemplate(nested, visited);
      if (found) {
        return found;
      }
    }

    return null;
  }
}
